// Debug Helper para identificar problemas de componentes React
// Especialmente útil para detectar erros #130 (Element type is invalid)

export const validateReactComponent = (component, componentName) => {
  console.group(`🔍 Validando componente: ${componentName}`);

  try {
    // Verificações básicas
    if (component === undefined) {
      console.error(`❌ ${componentName} é undefined`);
      return false;
    }

    if (component === null) {
      console.error(`❌ ${componentName} é null`);
      return false;
    }

    // Verificar tipo
    const componentType = typeof component;
    console.log(`📝 Tipo: ${componentType}`);

    if (componentType === 'function' || componentType === 'symbol') {
      console.log(`✅ ${componentName} é uma função ou símbolo (componente válido)`);
      console.log(`📄 Nome da função: ${component.name || 'anônima'}`);
      return true;
    }

    if (componentType === 'object') {
      console.log(`📦 ${componentName} é um objeto`);

      // Verificar se é um componente React.forwardRef
      if (component.$$typeof) {
        console.log(`⚛️ ${componentName} é um componente React especial`);
        return true;
      }

      // Verificar se tem propriedade default (importação incorreta)
      if (component.default) {
        const defaultType = typeof component.default;
        console.log(`🔄 ${componentName} tem propriedade default (tipo: ${defaultType})`);

        if (defaultType === 'function') {
          console.warn(`⚠️ ${componentName} pode ter sido importado incorretamente`);
          console.warn(`   Tente: import ${componentName} from '...' ao invés de import { ${componentName} } from '...'`);
          return true; // Ainda é válido, mas importado incorretamente
        }
      }

      // Verificar se é um objeto com múltiplas exportações
      const keys = Object.keys(component);
      console.log(`🔑 Chaves do objeto:`, keys);

      if (keys.length > 0) {
        console.warn(`⚠️ ${componentName} é um objeto com múltiplas exportações`);
        console.warn(`   Isso pode causar erro React #130`);
        console.warn(`   Certifique-se de importar o componente correto`);
      }

      console.error(`❌ ${componentName} é um objeto, mas não é um componente React válido`);
      return false;
    }

    console.error(`❌ ${componentName} tem tipo inválido: ${componentType}`);
    return false;

  } catch (error) {
    console.error(`❌ Erro ao validar ${componentName}:`, error);
    return false;
  } finally {
    console.groupEnd();
  }
};

export const debugReactError130 = (error, componentStack) => {
  console.group('🚨 Debug React Error #130');
  console.error('Error:', error);
  console.error('Component Stack:', componentStack);

  console.log('🔍 Possíveis causas:');
  console.log('1. Importação incorreta (default vs named export)');
  console.log('2. Componente sendo passado como objeto ao invés de função');
  console.log('3. Componente lazy loading com problema');
  console.log('4. Componente com export inválido');

  console.log('🛠️ Soluções sugeridas:');
  console.log('1. Verificar todas as importações de componentes');
  console.log('2. Usar validateReactComponent() para testar componentes');
  console.log('3. Verificar se todos os exports são default ou named corretamente');
  console.log('4. Testar em modo desenvolvimento para ver erros completos');

  console.groupEnd();
};

export const validateAllComponents = (components) => {
  console.group('🔍 Validando todos os componentes');
  const results = {};

  Object.entries(components).forEach(([name, component]) => {
    results[name] = validateReactComponent(component, name);
  });

  const validComponents = Object.entries(results).filter(([_, isValid]) => isValid);
  const invalidComponents = Object.entries(results).filter(([_, isValid]) => !isValid);

  console.log(`✅ Componentes válidos: ${validComponents.length}`);
  if (validComponents.length > 0) {
    console.log('Lista de componentes válidos:', validComponents.map(([name]) => name));
  }

  console.log(`❌ Componentes inválidos: ${invalidComponents.length}`);
  if (invalidComponents.length > 0) {
    console.error('Lista de componentes inválidos:', invalidComponents.map(([name]) => name));
  }

  console.groupEnd();

  return results;
};

// Hook para detectar erros em tempo de execução
export const useErrorDetection = () => {
  const detectError130 = (error) => {
    if (error?.message?.includes('Element type is invalid') ||
      error?.message?.includes('Minified React error #130')) {
      debugReactError130(error);
      return true;
    }
    return false;
  };

  return { detectError130 };
};

// Função para testar componentes antes do render
export const safeRenderComponent = (Component, props = {}, componentName = 'Unknown') => {
  try {
    if (!validateReactComponent(Component, componentName)) {
      throw new Error(`Componente ${componentName} inválido`);
    }

    return Component(props);
  } catch (error) {
    console.error(`❌ Erro ao renderizar ${componentName}:`, error);

    // Retornar componente de fallback
    return () => (
      <div style={{
        padding: '10px',
        border: '1px solid red',
        borderRadius: '4px',
        backgroundColor: '#ffe6e6'
      }}>
        <strong>Erro: {componentName}</strong>
        <p>Componente não pôde ser renderizado</p>
      </div>
    );
  }
};

export default {
  validateReactComponent,
  debugReactError130,
  validateAllComponents,
  useErrorDetection,
  safeRenderComponent
}; 